// The help, as it ships (_release/help), in a pane beside the program: F1
// opens the page of the command at the cursor, as in the desktop IDE, and an
// example a help page links to opens in the IDE as a project instead of as a
// text file. Home, Back and Forward move through the pages shown, as the
// desktop IDE's toolbar buttons do.

import { versioned } from "./site.js";

export class Help {
  // frame: the help pane's iframe, on this page's site. onExample(path) opens
  // the example a program the help links to belongs to, by the program's path
  // from the IDE's folder, such as help/commands/3d_examples/X.bb or
  // samples/shaders/showcase/45_fur.bb.
  // onNavigate() hears of each page shown.
  constructor({ frame, base, onExample, onNavigate = () => {} }) {
    this.frame = frame;
    this.base = new URL(base, location.href);
    this.onExample = onExample;
    this.onNavigate = onNavigate;
    this.pages = {};
    // The pages shown, and which of them shows. Back and Forward keep to the
    // frame's own pages: the frame's history.back() could leave the IDE.
    this.visited = [];
    this.at = -1;
    this.moving = false;
    frame.addEventListener("load", () => {
      this.attach();
      this.record();
    });
  }

  async load() {
    this.pages = await fetch(versioned(new URL("commands.json", this.base))).then((response) => response.json());
  }

  // The page of a command, or null.
  pageFor(word) {
    const page = word && this.pages[word.toLowerCase()];
    return page ? new URL(page, this.base).href : null;
  }

  // A page by its path in the help, such as language/lang_ref_enums.html.
  pageAt(path) { return new URL(path, this.base).href; }

  get home() { return new URL("index.html", this.base).href; }

  // The page shown, or null before the first.
  get location() {
    try {
      const href = this.frame.contentWindow.location.href;
      return href === "about:blank" ? null : href;
    } catch {
      return null;
    }
  }

  show(url = this.home) {
    if (this.location !== url) this.frame.src = url;
  }

  get canGoBack() { return this.at > 0; }
  get canGoForward() { return this.at < this.visited.length - 1; }
  back() { this.go(this.at - 1); }
  forward() { this.go(this.at + 1); }

  go(index) {
    if (index < 0 || index >= this.visited.length) return;
    this.at = index;
    this.moving = true;
    this.frame.contentWindow.location.replace(this.visited[index]);
  }

  // Each page shown is a step in Back and Forward, unless they showed it.
  record() {
    const url = this.location;
    if (!url) return;
    if (this.moving) {
      this.moving = false;
    } else if (url !== this.visited[this.at]) {
      this.visited.splice(this.at + 1, Infinity, url);
      this.at = this.visited.length - 1;
    }
    this.onNavigate();
  }

  // Example links open in the IDE. The help is on this page's site, so its
  // documents can be reached, and so can those of the frames in them: the
  // Command Reference shows each command's page in a frame of its index.
  attach(frame = this.frame) {
    let document;
    try {
      document = frame.contentDocument;
    } catch {
      return;
    }
    if (!document) return;
    const root = new URL(".", location.href).pathname;
    document.addEventListener("click", (event) => {
      const link = event.target.closest && event.target.closest("a[href]");
      if (!link) return;
      const url = new URL(link.href);
      if (url.origin !== location.origin || !/\.bb$/i.test(url.pathname) || !url.pathname.startsWith(root)) return;
      event.preventDefault();
      this.onExample(decodeURIComponent(url.pathname.slice(root.length)));
    });
    for (const inner of document.querySelectorAll("iframe, frame")) {
      inner.addEventListener("load", () => this.attach(inner));
      if (inner.contentDocument && inner.contentDocument.readyState === "complete") this.attach(inner);
    }
  }
}
